<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CopilotAPI from 'dashboard/api/integrations/copilot';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'insertReply']);
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');

const activeTab = ref('coaching');
const isLoading = ref(false);
const coaching = ref(null);
const suggestions = ref(null);

const conversationId = computed(() => currentChat.value?.id);

const sentimentColor = computed(() => {
  const map = {
    positive: 'text-n-teal-11',
    neutral: 'text-n-slate-11',
    hesitant: 'text-n-amber-11',
    negative: 'text-n-ruby-11',
  };
  return map[coaching.value?.customer_sentiment] || 'text-n-slate-11';
});

const sentimentLabel = computed(() => {
  const map = {
    positive: t('COPILOT.SENTIMENT.POSITIVE'),
    neutral: t('COPILOT.SENTIMENT.NEUTRAL'),
    hesitant: t('COPILOT.SENTIMENT.HESITANT'),
    negative: t('COPILOT.SENTIMENT.NEGATIVE'),
  };
  return (
    map[coaching.value?.customer_sentiment] ||
    coaching.value?.customer_sentiment
  );
});

const severityColor = severity => {
  const map = {
    high: 'bg-n-ruby-3 text-n-ruby-11',
    medium: 'bg-n-amber-3 text-n-amber-11',
    low: 'bg-n-slate-3 text-n-slate-11',
  };
  return map[severity] || 'bg-n-slate-3 text-n-slate-11';
};

const toneIcon = tone => {
  const map = {
    friendly: 'i-lucide-smile',
    professional: 'i-lucide-briefcase',
    urgent: 'i-lucide-zap',
    empathetic: 'i-lucide-heart',
    assertive: 'i-lucide-shield',
  };
  return map[tone] || 'i-lucide-message-circle';
};

const fetchCoaching = async () => {
  if (!conversationId.value) return;
  isLoading.value = true;
  coaching.value = null;
  try {
    const { data } = await CopilotAPI.getCoaching(conversationId.value);
    coaching.value = data;
  } catch {
    useAlert(t('COPILOT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const fetchSuggestions = async () => {
  if (!conversationId.value) return;
  isLoading.value = true;
  suggestions.value = null;
  try {
    const { data } = await CopilotAPI.getReplySuggestions(conversationId.value);
    suggestions.value = data;
  } catch {
    useAlert(t('COPILOT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const switchTab = tab => {
  activeTab.value = tab;
  if (tab === 'coaching' && !coaching.value) fetchCoaching();
  if (tab === 'suggestions' && !suggestions.value) fetchSuggestions();
};

const useReply = message => {
  emit('insertReply', message);
  useAlert(t('COPILOT.REPLY_INSERTED'));
};

const refresh = () => {
  if (activeTab.value === 'coaching') fetchCoaching();
  else fetchSuggestions();
};

watch(
  () => props.show,
  val => {
    if (val && !coaching.value && !isLoading.value) {
      fetchCoaching();
    }
  }
);

watch(conversationId, () => {
  coaching.value = null;
  suggestions.value = null;
  if (props.show) fetchCoaching();
});
</script>

<template>
  <transition name="slide">
    <div
      v-if="show"
      class="flex flex-col w-80 border-l border-n-weak bg-n-solid-2 h-full overflow-hidden flex-shrink-0"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-3 border-b border-n-weak">
        <div class="flex items-center gap-2">
          <span class="i-lucide-bot text-n-brand text-lg" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('COPILOT.TITLE') }}
          </h3>
        </div>
        <div class="flex items-center gap-1">
          <Button
            v-tooltip.top="$t('COPILOT.REFRESH')"
            icon="i-lucide-refresh-cw"
            xs
            slate
            faded
            :class="{ 'animate-spin': isLoading }"
            @click="refresh"
          />
          <Button icon="i-lucide-x" xs slate faded @click="$emit('close')" />
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-n-weak">
        <button
          class="flex-1 py-2 text-xs font-medium transition-colors"
          :class="
            activeTab === 'coaching'
              ? 'text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="switchTab('coaching')"
        >
          {{ $t('COPILOT.TABS.COACHING') }}
        </button>
        <button
          class="flex-1 py-2 text-xs font-medium transition-colors"
          :class="
            activeTab === 'suggestions'
              ? 'text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="switchTab('suggestions')"
        >
          {{ $t('COPILOT.TABS.SUGGESTIONS') }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-3 space-y-3">
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex flex-col items-center justify-center py-12 gap-3"
        >
          <Spinner />
          <p class="text-xs text-n-slate-11">{{ $t('COPILOT.ANALYZING') }}</p>
        </div>

        <!-- Coaching Tab -->
        <template v-else-if="activeTab === 'coaching' && coaching">
          <!-- Sentiment -->
          <div class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('COPILOT.SENTIMENT.LABEL') }}
            </span>
            <span class="text-xs font-semibold" :class="sentimentColor">
              {{ sentimentLabel }}
            </span>
          </div>

          <!-- Situation Analysis -->
          <div class="p-3 rounded-lg bg-n-alpha-2">
            <h4 class="text-xs font-semibold text-n-slate-11 uppercase mb-1">
              {{ $t('COPILOT.SITUATION') }}
            </h4>
            <p class="text-sm text-n-slate-12">
              {{ coaching.situation_analysis }}
            </p>
          </div>

          <!-- Next Best Action -->
          <div class="p-3 rounded-lg bg-n-brand/10 border border-n-brand/20">
            <h4 class="text-xs font-semibold text-n-brand uppercase mb-1">
              {{ $t('COPILOT.NEXT_ACTION') }}
            </h4>
            <p class="text-sm text-n-slate-12 font-medium">
              {{ coaching.next_best_action }}
            </p>
          </div>

          <!-- Detected Objections -->
          <div v-if="coaching.detected_objections?.length">
            <h4 class="text-xs font-semibold text-n-slate-11 uppercase mb-2">
              {{ $t('COPILOT.OBJECTIONS') }}
            </h4>
            <div class="space-y-1.5">
              <div
                v-for="(obj, i) in coaching.detected_objections"
                :key="i"
                class="p-2 rounded-lg bg-n-alpha-2 flex items-start gap-2"
              >
                <span
                  class="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium"
                  :class="severityColor(obj.severity)"
                >
                  {{ obj.type }}
                </span>
                <p class="text-xs text-n-slate-12">{{ obj.objection }}</p>
              </div>
            </div>
          </div>

          <!-- Coaching Tips -->
          <div v-if="coaching.coaching_tips?.length">
            <h4 class="text-xs font-semibold text-n-slate-11 uppercase mb-2">
              {{ $t('COPILOT.TIPS') }}
            </h4>
            <div class="space-y-1.5">
              <div
                v-for="(tip, i) in coaching.coaching_tips"
                :key="i"
                class="flex items-start gap-2 p-2 rounded-lg bg-n-alpha-2"
              >
                <span
                  class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold"
                  :class="
                    tip.priority === 'high'
                      ? 'bg-n-ruby-3 text-n-ruby-11'
                      : tip.priority === 'medium'
                        ? 'bg-n-amber-3 text-n-amber-11'
                        : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{ i + 1 }}
                </span>
                <p class="text-xs text-n-slate-12">{{ tip.tip }}</p>
              </div>
            </div>
          </div>

          <!-- Buying Signals -->
          <div v-if="coaching.buying_signals?.length">
            <h4 class="text-xs font-semibold text-n-teal-11 uppercase mb-2">
              {{ $t('COPILOT.BUYING_SIGNALS') }}
            </h4>
            <ul class="space-y-1">
              <li
                v-for="(signal, i) in coaching.buying_signals"
                :key="i"
                class="flex items-center gap-1.5 text-xs text-n-slate-12"
              >
                <span
                  class="i-lucide-check-circle text-n-teal-11 flex-shrink-0"
                />
                {{ signal }}
              </li>
            </ul>
          </div>

          <!-- Risk Factors -->
          <div v-if="coaching.risk_factors?.length">
            <h4 class="text-xs font-semibold text-n-ruby-11 uppercase mb-2">
              {{ $t('COPILOT.RISKS') }}
            </h4>
            <ul class="space-y-1">
              <li
                v-for="(risk, i) in coaching.risk_factors"
                :key="i"
                class="flex items-center gap-1.5 text-xs text-n-slate-12"
              >
                <span
                  class="i-lucide-alert-triangle text-n-ruby-11 flex-shrink-0"
                />
                {{ risk }}
              </li>
            </ul>
          </div>
        </template>

        <!-- Suggestions Tab -->
        <template v-else-if="activeTab === 'suggestions' && suggestions">
          <div class="space-y-3">
            <div
              v-for="(sug, i) in suggestions.suggestions"
              :key="i"
              class="p-3 rounded-lg border border-n-weak bg-n-alpha-1 space-y-2"
            >
              <div class="flex items-center gap-2">
                <span :class="toneIcon(sug.tone)" class="text-n-slate-10" />
                <span class="text-xs font-medium text-n-slate-11 capitalize">
                  {{ sug.tone }}
                </span>
              </div>
              <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                {{ sug.message }}
              </p>
              <p class="text-xs text-n-slate-10 italic">
                {{ sug.strategy }}
              </p>
              <Button
                icon="i-lucide-copy-plus"
                :label="$t('COPILOT.USE_REPLY')"
                xs
                class="w-full"
                @click="useReply(sug.message)"
              />
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-else-if="!isLoading"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <span class="i-lucide-bot text-3xl text-n-slate-10 mb-2" />
          <p class="text-sm text-n-slate-11">
            {{ $t('COPILOT.EMPTY') }}
          </p>
        </div>
      </div>
    </div>
  </transition>
</template>
